/**
 * Deterministic rule engine (B2). Pure functions of `RuleContext` — usable in
 * browser and Node (no network / DB / clock dependency; inject via ctx).
 *
 * Contract (docs/CONTRACT.md §3):
 *   export function evaluate(ctx, runAt): RuleFinding[];
 *   export function gate(ctx): GateResult;  // before_document_issue
 *
 * Gate passes when: before_document_issue findings have
 *   BLOCK&open == 0  AND  every ERROR is acknowledged.
 *
 * Each predicate returns a `RuleFinding` (rule fires) or `null` (no issue).
 * Predicates are typed TS — NOT JSONLogic — so determinism is testable.
 */

import type {
  GateResult,
  RuleContext,
  RuleFinding,
  RuleRunAt,
} from "@/types";
import { MVP_RULES, RULE_BY_ID, type CatalogRule, type RuleId } from "./catalog";

const EPSILON = 0.005; // money/weight comparison tolerance.

/** Build a finding from a catalogue rule + optional detail/message override. */
function fire(
  rule: CatalogRule,
  ctx: RuleContext,
  detail?: RuleFinding["detail"],
  messageOverride?: string,
): RuleFinding {
  return {
    id: `${rule.rule_id}:${ctx.deal.id}`,
    deal_id: ctx.deal.id,
    rule_id: rule.rule_id,
    category: rule.category,
    severity: rule.severity,
    message: messageOverride ?? rule.message,
    detail,
    fix_suggestion: rule.fix_suggestion,
    reference: rule.reference,
    ai_explanation: null,
    status: "open",
  };
}

type Predicate = (rule: CatalogRule, ctx: RuleContext) => RuleFinding | null;

const productLines = (ctx: RuleContext) =>
  ctx.lines.filter((l) => l.item_kind === "product");

const isUS = (ctx: RuleContext) => ctx.deal.destination_country === "US";
const isIN = (ctx: RuleContext) => ctx.deal.destination_country === "IN";
const isTW = (ctx: RuleContext) => ctx.deal.destination_country === "TW";

/** Sea-only incoterm codes already covered by the specific IC_KOREAN_FOB_AIR rule. */
const FOB_AIR_COVERED = new Set(["FOB", "FAS", "CFR", "CIF"]);

/** Incoterms 2020 — the complete 11-rule set (GEN_INCOTERM_ENUM). */
const INCOTERMS_2020 = new Set([
  "EXW", "FCA", "CPT", "CIP", "DAP", "DPU", "DDP", // any mode (7)
  "FAS", "FOB", "CFR", "CIF", // sea & inland waterway (4)
]);

/**
 * ISO 4217 active currency codes (GEN_CURRENCY_ISO4217). Static reference data —
 * the standard changes rarely; unknown-but-valid edge cases surface as an ERROR
 * the user can correct (never a BLOCK).
 */
const ISO_4217 = new Set([
  "AED","AFN","ALL","AMD","ANG","AOA","ARS","AUD","AWG","AZN","BAM","BBD","BDT",
  "BGN","BHD","BIF","BMD","BND","BOB","BRL","BSD","BTN","BWP","BYN","BZD","CAD",
  "CDF","CHF","CLP","CNY","COP","CRC","CUP","CVE","CZK","DJF","DKK","DOP","DZD",
  "EGP","ERN","ETB","EUR","FJD","FKP","GBP","GEL","GHS","GIP","GMD","GNF","GTQ",
  "GYD","HKD","HNL","HTG","HUF","IDR","ILS","INR","IQD","IRR","ISK","JMD","JOD",
  "JPY","KES","KGS","KHR","KMF","KPW","KRW","KWD","KYD","KZT","LAK","LBP","LKR",
  "LRD","LSL","LYD","MAD","MDL","MGA","MKD","MMK","MNT","MOP","MRU","MUR","MVR",
  "MWK","MXN","MYR","MZN","NAD","NGN","NIO","NOK","NPR","NZD","OMR","PAB","PEN",
  "PGK","PHP","PKR","PLN","PYG","QAR","RON","RSD","RUB","RWF","SAR","SBD","SCR",
  "SDG","SEK","SGD","SHP","SLE","SOS","SRD","SSP","STN","SVC","SYP","SZL","THB",
  "TJS","TMT","TND","TOP","TRY","TTD","TWD","TZS","UAH","UGX","USD","UYU","UZS",
  "VES","VND","VUV","WST","XAF","XCD","XOF","XPF","YER","ZAR","ZMW","ZWG",
]);

/** Digit count of an HS code (separators stripped). */
const hsDigits = (hs: string) => hs.replace(/\D/g, "").length;

/** Predicate table keyed by rule_id. Missing entries are treated as no-op. */
const PREDICATES: Record<RuleId, Predicate> = {
  // --- cross-field -----------------------------------------------------------
  CF_LINE_AMOUNT: (rule, ctx) => {
    for (const l of ctx.lines) {
      if (l.line_amount == null) continue;
      const expected = l.quantity * l.unit_price;
      if (Math.abs(expected - l.line_amount) > EPSILON) {
        return fire(rule, ctx, {
          field: "line_amount",
          expected,
          actual: l.line_amount,
        });
      }
    }
    return null;
  },
  CF_QTY_PACKAGE: (rule, ctx) => {
    // Advisory: if every product line declares package_count, sanity-check it's > 0.
    const lines = productLines(ctx);
    const bad = lines.find(
      (l) => l.package_count != null && l.package_count <= 0,
    );
    return bad
      ? fire(rule, ctx, { field: "package_count", actual: bad.package_count })
      : null;
  },

  // --- amount / weight -------------------------------------------------------
  AW_NET_GT_GROSS: (rule, ctx) => {
    for (const l of ctx.lines) {
      if (l.net_weight_kg == null || l.gross_weight_kg == null) continue;
      if (l.net_weight_kg - l.gross_weight_kg > EPSILON) {
        return fire(rule, ctx, {
          field: "weight",
          expected: `net <= gross`,
          actual: `${l.net_weight_kg} > ${l.gross_weight_kg}`,
        });
      }
    }
    return null;
  },
  AW_WEIGHT_UNIT_MIX: (rule, ctx) => {
    const units = new Set(ctx.lines.map((l) => l.weight_unit));
    return units.size > 1
      ? fire(rule, ctx, { field: "weight_unit", actual: [...units].join(", ") })
      : null;
  },
  AW_CURRENCY_MISSING: (rule, ctx) =>
    ctx.deal.currency && ctx.deal.currency.trim().length > 0
      ? null
      : fire(rule, ctx, { field: "currency" }),

  // --- doc missing -----------------------------------------------------------
  DM_REQUIRED_SET: (rule, ctx) => {
    const issuedTypes = new Set(ctx.documents.map((d) => d.doc_type));
    const missing = ctx.country.required_documents
      .filter((d) => d.requirement === "required")
      .map((d) => d.doc_type)
      .filter((t) => !issuedTypes.has(t));
    return missing.length
      ? fire(rule, ctx, { field: "required_documents", actual: missing })
      : null;
  },
  EXP_HS_UNVERIFIED: (rule, ctx) => {
    // Line items carry no verified flag, so cross-reference the backing product's
    // hs_verified. Fire only when an HS code in play is NOT broker-verified —
    // NOT merely "an HS code is present" (that was a false positive).
    const verifiedById = new Map(
      ctx.products.map((p) => [p.id, p.hs_verified]),
    );
    // Product-level: a product has an HS code but it is unverified.
    const productUnverified = ctx.products.some(
      (p) => p.hs_code != null && p.hs_code !== "" && !p.hs_verified,
    );
    // Line-level: a line carries an HS code whose backing product is unverified
    // (or has no resolvable product — treat as unverified, conservative).
    const lineUnverified = productLines(ctx).some(
      (l) =>
        l.hs_code != null &&
        l.hs_code !== "" &&
        verifiedById.get(l.product_id ?? "") !== true,
    );
    return productUnverified || lineUnverified ? fire(rule, ctx) : null;
  },

  // --- incoterm --------------------------------------------------------------
  IC_KOREAN_FOB_AIR: (rule, ctx) => {
    const seaOnly =
      ctx.incoterm?.transport_mode === "sea" ||
      ["FOB", "FAS", "CFR", "CIF"].includes(ctx.deal.incoterm_id ?? "");
    const nonSeaMode =
      ctx.deal.transport_mode != null &&
      ctx.deal.transport_mode !== "sea" &&
      ctx.deal.transport_mode !== "multimodal";
    return seaOnly && nonSeaMode
      ? fire(rule, ctx, {
          field: "incoterm_id",
          expected: "FCA (any mode)",
          actual: `${ctx.deal.incoterm_id} + ${ctx.deal.transport_mode}`,
        })
      : null;
  },
  IC_NAMED_PLACE_VAGUE: (rule, ctx) =>
    ctx.deal.incoterm_id &&
    (!ctx.deal.incoterm_named_place ||
      ctx.deal.incoterm_named_place.trim() === "")
      ? fire(rule, ctx, { field: "incoterm_named_place" })
      : null,
  IC_VERSION_MISSING: (rule, ctx) =>
    ctx.deal.incoterm_id &&
    (!ctx.deal.incoterm_version || ctx.deal.incoterm_version.trim() === "")
      ? fire(rule, ctx, { field: "incoterm_version" })
      : null,
  IC_MODE_MISMATCH: (rule, ctx) => {
    const seaOnly = ctx.incoterm?.transport_mode === "sea";
    // Avoid double-firing with the more specific IC_KOREAN_FOB_AIR, which already
    // covers FOB/FAS/CFR/CIF + non-sea mode. Only surface the generic message for
    // a sea-only incoterm NOT covered by that rule.
    const coveredByFobAir = FOB_AIR_COVERED.has(ctx.deal.incoterm_id ?? "");
    return seaOnly &&
      !coveredByFobAir &&
      ctx.deal.transport_mode != null &&
      ctx.deal.transport_mode !== "sea"
      ? fire(rule, ctx, {
          field: "transport_mode",
          expected: "sea",
          actual: ctx.deal.transport_mode,
        })
      : null;
  },
  IC_DDP_FOREIGN_IOR: (rule, ctx) =>
    ctx.deal.incoterm_id === "DDP" ? fire(rule, ctx) : null,

  // --- importer gate (generalised, v2) ----------------------------------------
  REQ_IMPORTER_ROLE: (rule, ctx) => {
    // Data-driven BLOCK (DP-7: assigning a partner is exporter-controlled).
    // Legacy US roles resolve from the deals FK columns; new roles from ctx.parties.
    const required = ctx.country.required_importer_roles ?? [];
    if (required.length === 0) return null;
    const missing = required.filter((role) => {
      if (role === "importer_of_record") return !ctx.deal.importer_of_record_id;
      if (role === "fda_initial_importer") return !ctx.deal.fda_initial_importer_id;
      return !ctx.parties?.[role];
    });
    return missing.length
      ? fire(rule, ctx, { field: "required_importer_roles", actual: missing })
      : null;
  },

  // --- US importer gate ------------------------------------------------------
  ERR_USAGENT_NOT_IMPORTER: (rule, ctx) => {
    // WARN if the chosen Initial Importer's only declared US role is us_agent.
    const imp = ctx.partners.fda_initial_importer;
    if (!isUS(ctx) || !imp) return null;
    const hasAgent = imp.roles.includes("us_agent");
    return hasAgent && !imp.is_fda_initial_importer ? fire(rule, ctx) : null;
  },
  ERR_DIRECT_CLINIC_IOR: (rule, ctx) =>
    isUS(ctx) &&
    ctx.deal.importer_structure === "direct" &&
    ctx.deal.importer_of_record_id != null
      ? fire(rule, ctx)
      : null,
  ERR_ENTRY_IDS_MISSING: (rule, ctx) => {
    if (!isUS(ctx)) return null;
    const missing = ctx.products
      .map((p) => {
        const r = p.regulatory ?? {};
        const gaps: string[] = [];
        if (!r.fda_fei) gaps.push("FEI");
        if (!r.fda_product_code) gaps.push("product_code");
        if (!r.fda_510k_number) gaps.push("510k");
        return gaps.length ? { model: p.model_name, gaps } : null;
      })
      .filter((x): x is { model: string; gaps: string[] } => x != null);
    return missing.length
      ? fire(rule, ctx, { field: "regulatory", actual: missing })
      : null;
  },
  ERR_LISTING_INACTIVE: (rule, ctx) => {
    if (!isUS(ctx)) return null;
    const inactive = ctx.products.find(
      (p) => p.regulatory?.fda_listing_active === false,
    );
    return inactive
      ? fire(rule, ctx, { field: "fda_listing_active", actual: inactive.model_name })
      : null;
  },

  // --- tariff guard (ack only; NO rate detection, D-2) -----------------------
  TG_TARIFF_ACK: (rule, ctx) =>
    ctx.deal.tariff_ack ? null : fire(rule, ctx),

  // --- payment ---------------------------------------------------------------
  PAY_BEFORE_PACK: (rule, ctx) =>
    !ctx.deal.payment_received ? fire(rule, ctx) : null,

  // ==== v2 (export-workflow-v2) ================================================
  // --- generic format / presence ----------------------------------------------
  GEN_LINES_PRESENT: (rule, ctx) =>
    productLines(ctx).length === 0 ? fire(rule, ctx) : null,
  GEN_CURRENCY_ISO4217: (rule, ctx) => {
    const c = ctx.deal.currency?.trim();
    if (!c) return null; // emptiness is AW_CURRENCY_MISSING's job.
    return ISO_4217.has(c.toUpperCase()) && c === c.toUpperCase()
      ? null
      : fire(rule, ctx, { field: "currency", actual: c });
  },
  GEN_INCOTERM_ENUM: (rule, ctx) => {
    const code = ctx.deal.incoterm_id;
    if (!code) return null;
    return INCOTERMS_2020.has(code)
      ? null
      : fire(rule, ctx, { field: "incoterm_id", actual: code });
  },
  GEN_HS_FORMAT: (rule, ctx) => {
    const bad = productLines(ctx).find(
      (l) => l.hs_code && hsDigits(l.hs_code) < 6,
    );
    return bad
      ? fire(rule, ctx, { field: "hs_code", actual: bad.hs_code, expected: "≥ 6 digits" })
      : null;
  },
  GEN_ORIGIN_PRESENT: (rule, ctx) => {
    const referenced = new Set(
      productLines(ctx).map((l) => l.product_id).filter(Boolean),
    );
    const missing = ctx.products.filter(
      (p) => referenced.has(p.id) && (!p.country_of_origin || p.country_of_origin.trim() === ""),
    );
    return missing.length
      ? fire(rule, ctx, { field: "country_of_origin", actual: missing.map((p) => p.model_name) })
      : null;
  },
  GEN_SIGNATURE_READY: (rule, ctx) =>
    ctx.shipper && !ctx.shipper.signature_image_ref ? fire(rule, ctx) : null,
  GEN_PKG_COUNT: (rule, ctx) => {
    const lines = productLines(ctx);
    if (lines.length === 0) return null;
    return lines.every((l) => l.package_count == null)
      ? fire(rule, ctx, { field: "package_count" })
      : null;
  },
  GEN_INSURANCE_REQUIRED: (rule, ctx) => {
    const sellerInsures = ctx.deal.incoterm_id === "CIF" || ctx.deal.incoterm_id === "CIP";
    return sellerInsures && !ctx.deal.insurance_policy_no
      ? fire(rule, ctx, { field: "insurance_policy_no" })
      : null;
  },

  // --- India -------------------------------------------------------------------
  IN_AGENT_MD15: (rule, ctx) => {
    if (!isIN(ctx)) return null;
    const agent = ctx.parties?.authorized_agent;
    if (!agent) return null; // absence itself is REQ_IMPORTER_ROLE's BLOCK.
    return agent.importer_ids?.md15_no ? null : fire(rule, ctx, { field: "importer_ids.md15_no" });
  },
  IN_IMPORTER_IDS: (rule, ctx) => {
    if (!isIN(ctx)) return null;
    const agent = ctx.parties?.authorized_agent;
    if (!agent) return null;
    const gaps: string[] = [];
    if (!agent.importer_ids?.iec) gaps.push("IEC");
    if (!agent.importer_ids?.gstin) gaps.push("GSTIN");
    return gaps.length ? fire(rule, ctx, { field: "importer_ids", actual: gaps }) : null;
  },
  IN_HSN8: (rule, ctx) => {
    if (!isIN(ctx)) return null;
    const bad = productLines(ctx).find(
      (l) => l.hs_code && hsDigits(l.hs_code) < 8,
    );
    return bad
      ? fire(rule, ctx, { field: "hs_code", actual: bad.hs_code, expected: "8 digits (HSN)" })
      : null;
  },
  IN_CEPA_CO_MISSING: (rule, ctx) => {
    if (!isIN(ctx) || !ctx.deal.claim_preferential) return null;
    const has = ctx.documents.some((d) => d.doc_type === "cepa_co_worksheet");
    return has ? null : fire(rule, ctx, { field: "documents", expected: "cepa_co_worksheet" });
  },
  IN_CO_STALE: (rule, ctx) => {
    if (!isIN(ctx)) return null;
    const ws = ctx.documents.find((d) => d.doc_type === "cepa_co_worksheet");
    if (!ws) return null; // absence is IN_CEPA_CO_MISSING's job.
    return ctx.deal.updated_at > ws.created_at
      ? fire(rule, ctx, {
          field: "cepa_co_worksheet",
          expected: `re-issue after ${ctx.deal.updated_at}`,
          actual: ws.created_at,
        })
      : null;
  },

  // --- Taiwan --------------------------------------------------------------------
  TW_LAR_TFDA: (rule, ctx) => {
    if (!isTW(ctx)) return null;
    const lar = ctx.parties?.lar;
    if (!lar) return null; // absence itself is REQ_IMPORTER_ROLE's BLOCK.
    return lar.importer_ids?.tfda_license_no
      ? null
      : fire(rule, ctx, { field: "importer_ids.tfda_license_no" });
  },
  TW_CI_IMPORT_LICENSE: (rule, ctx) => {
    if (!isTW(ctx)) return null;
    const candidates = [
      ctx.parties?.lar,
      ctx.partners.consignee,
      ctx.partners.buyer,
    ];
    const has = candidates.some((p) => p?.importer_ids?.import_license_no);
    return has ? null : fire(rule, ctx, { field: "importer_ids.import_license_no" });
  },
  TW_CN_LABEL: (rule, ctx) => (isTW(ctx) ? fire(rule, ctx) : null),
  TW_NO_FTA_CO: (rule, ctx) =>
    isTW(ctx) && ctx.deal.claim_preferential
      ? fire(rule, ctx, { field: "claim_preferential", expected: false, actual: true })
      : null,

  // --- L/C minimal (clock injected via ctx.today — engine stays pure) ----------
  LC_EXPIRY: (rule, ctx) => {
    if (ctx.paymentTerm?.type !== "LC") return null;
    const { lc_expiry_date } = ctx.deal;
    if (!lc_expiry_date || !ctx.today) return null;
    return ctx.today > lc_expiry_date
      ? fire(rule, ctx, { field: "lc_expiry_date", actual: lc_expiry_date, expected: `>= ${ctx.today}` })
      : null;
  },
  LC_LATE_SHIPMENT: (rule, ctx) => {
    if (ctx.paymentTerm?.type !== "LC") return null;
    const { lc_latest_shipment_date, status } = ctx.deal;
    if (!lc_latest_shipment_date || !ctx.today) return null;
    const preShipment = status === "quote" || status === "order" || status === "ship_prep";
    return preShipment && ctx.today > lc_latest_shipment_date
      ? fire(rule, ctx, {
          field: "lc_latest_shipment_date",
          actual: lc_latest_shipment_date,
          expected: `>= ${ctx.today}`,
        })
      : null;
  },
};

/**
 * Evaluate all MVP rules whose `run_at` includes `runAt`.
 * Pure; deterministic; order follows the catalogue.
 */
export function evaluate(ctx: RuleContext, runAt: RuleRunAt): RuleFinding[] {
  const findings: RuleFinding[] = [];
  for (const rule of MVP_RULES) {
    if (!rule.enabled || !rule.mvp_enabled) continue;
    if (!rule.run_at.includes(runAt)) continue;
    const predicate = PREDICATES[rule.rule_id];
    if (!predicate) continue; // catalogue entry without an implemented predicate -> skip.
    const finding = predicate(rule, ctx);
    if (finding) findings.push(finding);
  }
  return findings;
}

/**
 * Issue gate (before_document_issue). Passes when BLOCK&open == 0 AND every
 * ERROR is acknowledged. `acknowledgedRuleIds` lets the caller mark ERRORs as
 * acknowledged (the engine itself is stateless).
 */
export function gate(
  ctx: RuleContext,
  acknowledgedRuleIds: ReadonlySet<string> = new Set(),
): GateResult {
  const findings = evaluate(ctx, "before_document_issue").map((f) =>
    f.severity === "ERROR" && acknowledgedRuleIds.has(f.rule_id)
      ? { ...f, status: "acknowledged" as const }
      : f,
  );

  const blocks = findings.filter(
    (f) => f.severity === "BLOCK" && f.status === "open",
  ).length;
  const errors = findings.filter((f) => f.severity === "ERROR").length;
  const openErrors = findings.filter(
    (f) => f.severity === "ERROR" && f.status === "open",
  ).length;
  const warns = findings.filter((f) => f.severity === "WARN").length;
  const infos = findings.filter((f) => f.severity === "INFO").length;

  return {
    findings,
    blocks,
    errors,
    warns,
    infos,
    passed: blocks === 0 && openErrors === 0,
  };
}

export { MVP_RULES, RULE_BY_ID };
